import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAttractions } from '../../lib/queries';
import type { Attraction } from '../../lib/types';
import { AttractionDetailPanel } from '../attractions/AttractionDetailPanel';
import { Carousel, CarouselItem } from './Carousel';

/**
 * "Your Adventure Starts Here" — spec §7.1. A carousel of featured attractions
 * (`is_featured = true`, admin-driven per the AC). There is no standalone attraction page
 * (§20.1-16), so a card opens the shared AttractionDetailPanel in a modal overlay — the same
 * detail the MapExplore shows.
 */
export function FeaturedAttractions() {
  const { t } = useTranslation();
  const { data: attractions = [], isLoading } = useAttractions({ is_featured: true });
  const [selected, setSelected] = useState<Attraction | null>(null);

  if (isLoading || attractions.length === 0) return null;

  return (
    <>
      <Carousel title={t('home.adventureTitle')} subtitle={t('home.adventureSubtitle')}>
        {attractions.map((attraction) => (
          <CarouselItem key={attraction.id}>
            <FeaturedCard attraction={attraction} onOpen={() => setSelected(attraction)} />
          </CarouselItem>
        ))}
      </Carousel>

      {selected && <AttractionModal attraction={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function FeaturedCard({ attraction, onOpen }: { attraction: Attraction; onOpen: () => void }) {
  const location = attraction.municipality?.name ?? attraction.region?.name;
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-panel shadow-soft dark:shadow-soft-dark">
      <div className="aspect-[16/10] bg-content/5">
        {attraction.image && (
          <img src={attraction.image} alt={attraction.name} className="h-full w-full object-cover" />
        )}
      </div>
      <div className="flex items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-content">{attraction.name}</h3>
          {location && (
            <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-content-muted">
              <span aria-hidden="true">◍</span>
              {location}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onOpen}
          aria-label={attraction.name}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-accent text-accent-contrast transition-opacity hover:opacity-90"
        >
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Modal overlay wrapping the shared AttractionDetailPanel. The panel is normally an inline
 * grid column beside the map; here a backdrop + centred card give it a standalone detail
 * presentation, with Escape-to-close and background scroll lock.
 */
function AttractionModal({ attraction, onClose }: { attraction: Attraction; onClose: () => void }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('home.adventureTitle')}
    >
      <button type="button" aria-hidden="true" tabIndex={-1} onClick={onClose} className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-3xl bg-panel shadow-soft dark:shadow-soft-dark">
        <AttractionDetailPanel attraction={attraction} onClose={onClose} />
      </div>
    </div>
  );
}
